import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Trophy, MapPin, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicView() {
  const navigate = useNavigate();
  const [fechaId, setFechaId] = useState(null);
  const [fecha, setFecha] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get fechaId from URL path
    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];
    setFechaId(id);
  }, []);

  useEffect(() => {
    if (fechaId) {
      loadFecha();
      // Auto-refresh every 5 seconds
      const interval = setInterval(loadFecha, 5000);
      return () => clearInterval(interval);
    }
  }, [fechaId]);

  const loadFecha = async () => {
    try {
      const response = await axios.get(`${API}/public/fecha/${fechaId}`);
      setFecha(response.data);
    } catch (error) {
      toast.error('Error al cargar la fecha');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPartido = (partidoId) => {
    navigate(`/public/partido/${partidoId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!fecha) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="p-8 text-center">
          <p className="text-slate-600">Fecha no encontrada</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">{/* Header removed - public view */}

      <div className="container mx-auto px-2 py-4">
        <div className="bg-white rounded-lg shadow-sm p-3 mb-4" data-testid="fecha-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">{fecha.descripcion}</h1>
              <p className="text-sm text-slate-600">{fecha.liga_info?.name} - {fecha.fecha}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fecha.canchas?.map((cancha) => (
            <Card key={cancha.id} className="p-4 shadow-sm hover:shadow-md transition-shadow border-0" data-testid="cancha-public-card">
              <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2 border-b pb-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                {cancha.nombre_cancha}
              </h3>

              <div className="space-y-3">
                {cancha.partidos?.map((partido) => (
                  <div
                    key={partido.id}
                    className="p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                    data-testid="partido-public-item"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">{partido.hora}</span>
                      </div>
                      {/* Badge de Estado */}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        partido.estado === 'finalizado' 
                          ? 'bg-slate-200 text-slate-700' 
                          : partido.estado === 'en_progreso' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        {partido.estado === 'finalizado' ? 'Finalizado' : partido.estado === 'en_progreso' ? 'Vivo' : 'Pendiente'}
                      </span>
                    </div>

                    {/* Badge de Fase - Centro */}
                    {partido.fase && (
                      <div className="text-center mb-2">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                          partido.fase === 'Resultado Final' 
                            ? 'bg-slate-600 text-white' 
                            : partido.fase === '1er Tiempo' || partido.fase === '2do Tiempo'
                              ? 'bg-green-600 text-white'
                              : partido.fase === 'Entretiempo'
                                ? 'bg-orange-500 text-white'
                                : partido.fase === 'Por Comenzar'
                                  ? 'bg-blue-500 text-white'
                                  : partido.fase === 'Atrasado'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-slate-400 text-white'
                        }`}>
                          {partido.fase}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        {partido.rival1_info?.logo && (
                          <img src={partido.rival1_info.logo} alt="" className="w-7 h-7 object-cover rounded flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium">{partido.rival1_nombre_corto}</span>
                      </div>
                      
                      {partido.resultado && (partido.estado === 'en_progreso' || partido.estado === 'finalizado') ? (
                        <div className="text-lg font-bold text-emerald-600 px-2 flex-shrink-0">
                          {partido.resultado.goles_local} - {partido.resultado.goles_visita}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 px-2 flex-shrink-0">vs</span>
                      )}

                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <span className="text-sm font-medium text-right">{partido.rival2_nombre_corto}</span>
                        {partido.rival2_info?.logo && (
                          <img src={partido.rival2_info.logo} alt="" className="w-7 h-7 object-cover rounded flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    <Button
                      data-testid={`view-partido-${partido.id}`}
                      onClick={() => handleViewPartido(partido.id)}
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                    >
                      <Eye className="w-3 h-3" />
                      Ver
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
